import React, { useState } from 'react';
import styled from 'styled-components';
import TileGrid from './TileGrid';

// Define icon components with proper typing
// Import or create these icons properly
const FiSearch = () => <span>🔍</span>; // Replace with actual import
const FiGrid = () => <span>⏹️</span>; // Replace with actual import
const FiList = () => <span>📋</span>; // Replace with actual import
const FiStar = () => <span>⭐</span>; // Replace with actual import
const FiClock = () => <span>🕒</span>; // Replace with actual import
const FiExternalLink = () => <span>↗️</span>; // Replace with actual import
const FiUser = () => <span>👤</span>; // Replace with actual import
const FiSettings = () => <span>⚙️</span>; // Replace with actual import
const FiHelpCircle = () => <span>❓</span>; // Replace with actual import
const FiLogOut = () => <span>🚪</span>; // Replace with actual import
const FiFilter = () => <span>🔧</span>; // Replace with actual import
const FiPlus = () => <span>➕</span>; // Replace with actual import
const FiMenu = () => <span>☰</span>; // Replace with actual import
const FiChevronDown = () => <span>⌄</span>; // Replace with actual import
const FiBell = () => <span>🔔</span>; // Replace with actual import

// Or better, install and import from react-icons/fi:
// import { FiMenu, FiBell, /* ... other icons */ } from 'react-icons/fi';

// Mock user data
const user = {
  name: 'Alex Johnson',
  email: 'alex.johnson@company.com',
  avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Alex'
};

// Mock applications data
const allApplications = [
  { id: 1, name: 'Salesforce', category: 'CRM', starred: true, recent: true, color: '#00A1E0' },
  { id: 2, name: 'Slack', category: 'Communication', starred: true, recent: true, color: '#4A154B' },
  { id: 3, name: 'Jira', category: 'Productivity', starred: false, recent: true, color: '#0052CC' },
  { id: 4, name: 'Confluence', category: 'Productivity', starred: false, recent: false, color: '#172B4D' },
  { id: 5, name: 'Zoom', category: 'Communication', starred: true, recent: false, color: '#2D8CFF' },
  { id: 6, name: 'Workday', category: 'HR', starred: false, recent: false, color: '#0066CC' },
  { id: 7, name: 'Google Drive', category: 'Productivity', starred: false, recent: true, color: '#4285F4' },
  { id: 8, name: 'Microsoft Teams', category: 'Communication', starred: false, recent: true, color: '#6264A7' },
  { id: 9, name: 'Tableau', category: 'Analytics', starred: true, recent: false, color: '#E97627' },
  { id: 10, name: 'ServiceNow', category: 'ITSM', starred: false, recent: false, color: '#4DB848' },
];

const TileApp: React.FC = () => {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Filter applications based on search and category
  const filteredApplications = allApplications.filter(app => {
    const matchesSearch = app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         app.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = activeFilter === 'all' || 
                         (activeFilter === 'starred' && app.starred) ||
                         (activeFilter === 'recent' && app.recent) ||
                         (activeFilter === 'communication' && app.category === 'Communication') ||
                         (activeFilter === 'productivity' && app.category === 'Productivity');
    
    return matchesSearch && matchesFilter;
  });

  const categories = [
    { id: 'all', name: 'All Apps', count: allApplications.length },
    { id: 'starred', name: 'Starred', count: allApplications.filter(a => a.starred).length },
    { id: 'recent', name: 'Recently Used', count: allApplications.filter(a => a.recent).length },
    { id: 'communication', name: 'Communication', count: allApplications.filter(a => a.category === 'Communication').length },
    { id: 'productivity', name: 'Productivity', count: allApplications.filter(a => a.category === 'Productivity').length },
    { id: 'crm', name: 'CRM', count: allApplications.filter(a => a.category === 'CRM').length },
  ];

  return (
    <AppContainer>
      {/* Header */}
      <Header>
        <HeaderLeft>
          <LogoContainer>
            <Logo>My Apps</Logo>
            <LogoSubtitle>Application Portal</LogoSubtitle>
          </LogoContainer>
          <MobileMenuButton onClick={() => setShowMobileMenu(!showMobileMenu)}>
            <FiMenu />
          </MobileMenuButton>
        </HeaderLeft>

        <HeaderCenter>
          <SearchContainer>
            <FiSearch />
            <SearchInput 
              type="text" 
              placeholder="Search applications..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <FilterButton>
              <FiFilter />
            </FilterButton>
          </SearchContainer>
        </HeaderCenter>

        <HeaderRight>
          <NotificationButton>
            <FiBell />
            <NotificationBadge>3</NotificationBadge>
          </NotificationButton>
          <UserMenuContainer>
            <UserAvatar onClick={() => setShowUserMenu(!showUserMenu)}>
              <img src={user.avatar} alt={user.name} />
              <UserInfo>
                <UserName>{user.name}</UserName>
                <UserEmail>{user.email}</UserEmail>
              </UserInfo>
              <FiChevronDown />
            </UserAvatar>
            
            {showUserMenu && (
              <UserDropdown>
                <DropdownItem>
                  <FiUser /> Profile
                </DropdownItem>
                <DropdownItem>
                  <FiSettings /> Settings
                </DropdownItem>
                <DropdownItem>
                  <FiHelpCircle /> Help
                </DropdownItem>
                <DropdownDivider />
                <DropdownItem>
                  <FiLogOut /> Sign Out
                </DropdownItem>
              </UserDropdown>
            )}
          </UserMenuContainer>
        </HeaderRight>
      </Header>

      {/* Mobile Menu */}
      {showMobileMenu && (
        <MobileMenu>
          <MobileMenuItem>Dashboard</MobileMenuItem>
          <MobileMenuItem>Applications</MobileMenuItem>
          <MobileMenuItem>Settings</MobileMenuItem>
          <MobileMenuItem>Help</MobileMenuItem>
        </MobileMenu>
      )}

      {/* Main Content */}
      <MainContent>
        <PageHeader>
          <PageTitle>
            <h1>My Applications</h1>
            <PageSubtitle>Access all your applications from one place</PageSubtitle>
          </PageTitle>
          <PageActions>
            <AddAppButton>
              <FiPlus /> Add Application
            </AddAppButton>
            <ViewToggle>
              <ViewButton 
                $active={viewMode === 'grid'} 
                onClick={() => setViewMode('grid')}
              >
                <FiGrid />
              </ViewButton>
              <ViewButton 
                $active={viewMode === 'list'} 
                onClick={() => setViewMode('list')}
              >
                <FiList />
              </ViewButton>
            </ViewToggle>
          </PageActions>
        </PageHeader>

        {/* Categories */}
        <CategorySection>
          <CategoryTitle>Browse by Category</CategoryTitle>
          <CategoryTabs>
            {categories.map(category => (
              <CategoryTab 
                key={category.id}
                $active={activeFilter === category.id}
                onClick={() => setActiveFilter(category.id)}
              >
                {category.name}
                <CategoryCount $active={activeFilter === category.id}>{category.count}</CategoryCount>
              </CategoryTab>
            ))}
          </CategoryTabs>
        </CategorySection>

        {/* Your Custom Tile Grid */}
        <Section>
          <SectionHeader>
            <SectionTitle>AI Assistant Applications</SectionTitle>
            <SectionDescription>Custom AI tools for your business needs</SectionDescription>
          </SectionHeader>
          <TileGrid />
        </Section>

        {/* Standard Applications Grid/List */}
        <Section>
          <SectionHeader>
            <SectionTitle>All Applications</SectionTitle>
            <SectionDescription>
              {filteredApplications.length} of {allApplications.length} applications
            </SectionDescription>
          </SectionHeader>

          {viewMode === 'grid' ? (
            <ApplicationsGrid>
              {filteredApplications.map(app => (
                <AppCard key={app.id}>
                  <AppCardHeader>
                    <AppIcon style={{ backgroundColor: app.color }}>
                      {app.name.charAt(0)}
                    </AppIcon>
                    <AppActions>
                      <StarButton $starred={app.starred}>
                        <FiStar />
                      </StarButton>
                    </AppActions>
                  </AppCardHeader>
                  <AppCardBody>
                    <AppName>{app.name}</AppName>
                    <AppCategory>{app.category}</AppCategory>
                  </AppCardBody>
                  <AppCardFooter>
                    {app.recent && (
                      <RecentBadge>
                        <FiClock /> Recent
                      </RecentBadge>
                    )}
                    <LaunchButton>
                      <FiExternalLink /> Launch
                    </LaunchButton>
                  </AppCardFooter>
                </AppCard>
              ))}
            </ApplicationsGrid>
          ) : (
            <ApplicationsList>
              {filteredApplications.map(app => (
                <AppListItem key={app.id}>
                  <AppListIcon style={{ backgroundColor: app.color }}>
                    {app.name.charAt(0)}
                  </AppListIcon>
                  <AppListInfo>
                    <AppListName>{app.name}</AppListName>
                    <AppListCategory>{app.category}</AppListCategory>
                  </AppListInfo>
                  <AppListActions>
                    {app.recent && (
                      <RecentBadge>
                        <FiClock />
                      </RecentBadge>
                    )}
                    <StarButton $starred={app.starred}>
                      <FiStar />
                    </StarButton>
                    <LaunchButton>
                      <FiExternalLink />
                    </LaunchButton>
                  </AppListActions>
                </AppListItem>
              ))}
            </ApplicationsList>
          )}
        </Section>

        {/* Empty State */}
        {filteredApplications.length === 0 && (
          <EmptyState>
            <EmptyStateIcon>🔍</EmptyStateIcon>
            <EmptyStateTitle>No applications found</EmptyStateTitle>
            <EmptyStateDescription>
              Try adjusting your search or filter to find what you're looking for.
            </EmptyStateDescription>
            <ClearFiltersButton onClick={() => { setSearchQuery(''); setActiveFilter('all'); }}>
              Clear all filters
            </ClearFiltersButton>
          </EmptyState>
        )}
      </MainContent>

      {/* Footer */}
      <Footer>
        <FooterContent>
          <FooterLinks>
            <FooterLink href="#">Privacy Policy</FooterLink>
            <FooterLink href="#">Terms of Service</FooterLink>
            <FooterLink href="#">Support</FooterLink>
            <FooterLink href="#">Status</FooterLink>
          </FooterLinks>
          <FooterCopyright>© 2024 My Apps Portal. All rights reserved.</FooterCopyright>
        </FooterContent>
      </Footer>
    </AppContainer>
  );
};

// Styled Components with FIXED prop names (using $ prefix for transient props)
const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  height: 64px;
  background: white;
  border-bottom: 1px solid #e8e8e8;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  position: sticky;
  top: 0;
  z-index: 100;
  
  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Logo = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #3182ce;
  line-height: 1;
`;

const LogoSubtitle = styled.div`
  font-size: 12px;
  color: #718096;
  margin-top: 2px;
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 20px;
  color: #4a5568;
  cursor: pointer;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const HeaderCenter = styled.div`
  flex: 1;
  max-width: 600px;
  margin: 0 32px;
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  background: #f7fafc;
  border-radius: 8px;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  
  svg {
    color: #a0aec0;
    margin-right: 8px;
  }
`;

const SearchInput = styled.input`
  border: none;
  background: transparent;
  width: 100%;
  font-size: 14px;
  outline: none;
  
  &::placeholder {
    color: #a0aec0;
  }
`;

const FilterButton = styled.button`
  background: none;
  border: none;
  color: #718096;
  cursor: pointer;
  padding: 4px;
  margin-left: 8px;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const NotificationButton = styled.button`
  position: relative;
  background: none;
  border: none;
  color: #4a5568;
  font-size: 18px;
  cursor: pointer;
  padding: 8px;
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  background: #e53e3e;
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 5px;
  border-radius: 10px;
  min-width: 16px;
  text-align: center;
`;

const UserMenuContainer = styled.div`
  position: relative;
`;

const UserAvatar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  
  &:hover {
    background: #f7fafc;
  }
  
  img {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    object-fit: cover;
  }
  
  svg {
    color: #718096;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const UserName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
`;

const UserEmail = styled.div`
  font-size: 12px;
  color: #718096;
`;

const UserDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  padding: 8px 0;
  min-width: 200px;
  z-index: 1000;
  margin-top: 8px;
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: none;
  text-align: left;
  font-size: 14px;
  color: #2d3748;
  cursor: pointer;
  
  &:hover {
    background: #f7fafc;
  }
`;

const DropdownDivider = styled.div`
  height: 1px;
  background: #e2e8f0;
  margin: 8px 0;
`;

const MobileMenu = styled.div`
  position: fixed;
  top: 64px;
  left: 0;
  right: 0;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  padding: 16px;
  z-index: 99;
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const MobileMenuItem = styled.button`
  display: block;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  text-align: left;
  font-size: 16px;
  color: #2d3748;
  cursor: pointer;
  border-radius: 6px;
  
  &:hover {
    background: #f7fafc;
  }
`;

const MainContent = styled.main`
  flex: 1;
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 16px;
`;

const PageTitle = styled.div`
  h1 {
    font-size: 28px;
    font-weight: 700;
    color: #2d3748;
    margin: 0 0 4px 0;
  }
`;

const PageSubtitle = styled.div`
  font-size: 14px;
  color: #718096;
`;

const PageActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const AddAppButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #3182ce;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: #2c5282;
  }
`;

const ViewToggle = styled.div`
  display: flex;
  background: #f7fafc;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
`;

// FIXED: Using $ prefix for transient props to avoid DOM errors
interface ViewButtonProps {
  $active: boolean;
}

const ViewButton = styled.button<ViewButtonProps>`
  border: none;
  background: ${props => props.$active ? '#ffffff' : 'transparent'};
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: ${props => props.$active ? '#3182ce' : '#718096'};
  
  &:first-child {
    border-right: 1px solid #e2e8f0;
  }
`;

const CategorySection = styled.div`
  margin-bottom: 32px;
`;

const CategoryTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 12px;
`;

const CategoryTabs = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 8px;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 2px;
  }
`;

// FIXED: Using $ prefix for transient props
interface CategoryTabProps {
  $active: boolean;
}

const CategoryTab = styled.button<CategoryTabProps>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 8px;
  border: none;
  background: ${props => props.$active ? '#ebf8ff' : '#f7fafc'};
  color: ${props => props.$active ? '#3182ce' : '#4a5568'};
  font-size: 14px;
  font-weight: ${props => props.$active ? '600' : '400'};
  cursor: pointer;
  white-space: nowrap;
  border: 1px solid ${props => props.$active ? '#bee3f8' : 'transparent'};
  
  &:hover {
    background: ${props => props.$active ? '#ebf8ff' : '#edf2f7'};
  }
`;

// FIXED: Using $ prefix for transient props
interface CategoryCountProps {
  $active: boolean;
}

const CategoryCount = styled.span<CategoryCountProps>`
  background: ${props => props.$active ? '#3182ce' : '#e2e8f0'};
  color: ${props => props.$active ? 'white' : '#718096'};
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 12px;
`;

const Section = styled.section`
  margin-bottom: 40px;
`;

const SectionHeader = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 4px 0;
`;

const SectionDescription = styled.p`
  font-size: 14px;
  color: #718096;
  margin: 0;
`;

const ApplicationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  }
`;

const AppCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.1);
    border-color: #cbd5e0;
  }
`;

const AppCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const AppIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
  font-weight: 700;
`;

const AppActions = styled.div`
  display: flex;
  gap: 4px;
`;

// FIXED: Using $ prefix for transient props
interface StarButtonProps {
  $starred: boolean;
}

const StarButton = styled.button<StarButtonProps>`
  background: none;
  border: none;
  cursor: pointer;
  color: ${props => props.$starred ? '#f6ad55' : '#cbd5e0'};
  padding: 4px;
  
  &:hover {
    color: #f6ad55;
  }
`;

const AppCardBody = styled.div`
  margin-bottom: 16px;
`;

const AppName = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 4px 0;
`;

const AppCategory = styled.div`
  font-size: 14px;
  color: #718096;
`;

const AppCardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const RecentBadge = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #718096;
  background: #f7fafc;
  padding: 4px 8px;
  border-radius: 4px;
`;

const LaunchButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: #3182ce;
  text-decoration: none;
  padding: 6px 12px;
  border-radius: 6px;
  background: #ebf8ff;
  border: none;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: #bee3f8;
  }
`;

const ApplicationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AppListItem = styled.div`
  display: flex;
  align-items: center;
  background: white;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s;
  
  &:hover {
    background: #f7fafc;
    border-color: #cbd5e0;
  }
`;

const AppListIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  font-weight: 700;
  margin-right: 16px;
`;

const AppListInfo = styled.div`
  flex: 1;
`;

const AppListName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
`;

const AppListCategory = styled.div`
  font-size: 14px;
  color: #718096;
`;

const AppListActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  background: white;
  border-radius: 12px;
  border: 1px dashed #cbd5e0;
  text-align: center;
`;

const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const EmptyStateTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 8px 0;
`;

const EmptyStateDescription = styled.p`
  font-size: 14px;
  color: #718096;
  margin: 0 0 20px 0;
  max-width: 400px;
`;

const ClearFiltersButton = styled.button`
  background: #3182ce;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  
  &:hover {
    background: #2c5282;
  }
`;

const Footer = styled.footer`
  background: white;
  border-top: 1px solid #e2e8f0;
  padding: 24px;
  margin-top: 40px;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  
  @media (max-width: 768px) {
    text-align: center;
  }
`;

const FooterLinks = styled.div`
  display: flex;
  gap: 24px;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    justify-content: center;
    gap: 16px;
  }
`;

const FooterLink = styled.a`
  font-size: 14px;
  color: #718096;
  text-decoration: none;
  
  &:hover {
    color: #3182ce;
    text-decoration: underline;
  }
`;

const FooterCopyright = styled.div`
  font-size: 14px;
  color: #a0aec0;
`;

export default TileApp;